import { Build } from "@stencil/core";
import { APP_CONFIG } from "../../config";
import { DeviceState } from "./state";
import { CookiePreferences } from "../../services/Device/Device.interface";
import { onStateKeyChange, setState, state } from "..";
import {
  loadCookiesObservable,
} from "../../services/Device/device";
import {
  buildConsentPreferences,
  persistConsentPreferences,
} from "../../services/Device/cookieConsent";
import { Language } from "../../utils/i8n";
import { globalLanguageState } from "../../global/globalState";
import { combineLatest, distinctUntilChanged, map } from "rxjs";
import { ReportError } from "../error/effects";
import { setLogLevel } from "../../utils/logger";

function updateDeviceState(data: Partial<DeviceState>, effect: string) {
  setState("DEVICE", { DEVICE: { ...state.DEVICE, ...data } }, effect);
}

async function initializeSentry() {
  const { logger } = await import("../../utils/logger");
  if (Build.isBrowser) {
    const { initSentry } = await import("../../utils/sentry");
    initSentry({
      dsn: APP_CONFIG.sentry.dsn,
      environment: APP_CONFIG.environment,
      debug: false,
    });
    if (logger.getLevel() <= logger.levels.INFO) {
      logger.info("INIT: Initialized sentry");
    }
  }
  if (logger.getLevel() <= logger.levels.INFO) {
    logger.info("INIT: Sentry init skipped, pre-render build");
  }
}

async function initializeNavigation(
  effect: string
) {
  const { computeSiteNavigation } = await import(
    "../../services/Navigation"
  );
  const { updateDeviceNavigation } = await import("../navigation/effects");

  const { logger } = await import("../../utils/logger");
  updateDeviceNavigation(computeSiteNavigation(), effect);
  if (logger.getLevel() <= logger.levels.INFO) {
    logger.info("INIT: Initialized navigation");
  }
}

function initializeApp() {
  const effect = "initializeApp";
  const detectLangFromPath = () => {
    if (!Build.isBrowser) return null;
    const match = window.location.pathname.match(/^\/(en|nl)(\/|$)/);
    if (match) {
      return match[1].toUpperCase() as Language;
    }
    return null;
  };

  return combineLatest([
    loadCookiesObservable()
  ]).subscribe({
    next([cookies]) {
      setLogLevel(APP_CONFIG.environment);
      initializeSentry()
      initializeNavigation(effect).then(() => {
        const initialLang = detectLangFromPath() || APP_CONFIG.selectedLanguage;
        globalLanguageState.setSelectedLanguage(initialLang);
        updateDeviceState(
          {
            initialized: true,
            languageCode: initialLang,
            cookieAgreement: cookies.cookieAgreement,
            cookiePreferences: cookies.cookiePreferences || null,
          },
          effect
        );
      });
    },
    error(err) {
      ReportError("DEVICE", err, effect);
    },
  });
}

function buildPreferences(
  partial: Partial<CookiePreferences>,
): CookiePreferences {
  return buildConsentPreferences({
    updatedAt: new Date().toISOString(),
    ...partial,
  });
}

async function persistPreferences(
  preferences: CookiePreferences,
  effect: string,
) {
  updateDeviceState(
    {
      // Banner should close once a choice is made; analytics/marketing gating can use cookiePreferences.status.
      cookieAgreement: true,
      cookiePreferences: preferences,
    },
    effect
  );
  await persistConsentPreferences(preferences);
}

async function updateCookieAgreement(status: boolean) {
  if (status) {
    return acceptAllCookies();
  }
  return declineAllCookies();
}

async function acceptAllCookies() {
  const effect = "acceptAllCookies";
  const preferences = buildPreferences({
    status: "accepted",
    analytics: true,
    marketing: true,
  });
  await persistPreferences(preferences, effect);
}

async function declineAllCookies() {
  const effect = "declineAllCookies";
  const preferences = buildPreferences({
    status: "declined",
    analytics: false,
    marketing: false,
  });
  await persistPreferences(preferences, effect);
}

async function saveCustomCookiePreferences(options: {
  analytics: boolean;
  marketing: boolean;
}) {
  const effect = "saveCustomCookiePreferences";
  const preferences = buildPreferences({
    status: "custom",
    analytics: options.analytics,
    marketing: options.marketing,
  });
  await persistPreferences(preferences, effect);
}

function updateLanguage(
  lang: string
) {
  const effect = "updateNetworkStatusEffect";
  const payload: Pick<DeviceState, 'languageCode'> = { languageCode: lang as Language };
  updateDeviceState(payload, effect);
  globalLanguageState.setSelectedLanguage(lang);

  // Sync URL with selected language
  if (Build.isBrowser) {
    const langPrefix = lang.toLowerCase();
    const path = window.location.pathname;
    const search = window.location.search || '';
    const hash = window.location.hash || '';

    // Remove existing language prefix
    const basePath = path.replace(/^\/(en|nl)\//, '/').replace(/^\/(en|nl)$/, '/');

    const newPath = langPrefix === 'fr'
      ? basePath
      : `/${langPrefix}${basePath}`;

    const newUrl = `${newPath}${search}${hash}`;

    if (newUrl !== `${path}${search}${hash}`) {
      // Use full navigation so router/data reload in the new locale
      window.location.assign(newUrl);
    }
  }
};

function onLanguageChange() {
  return onStateKeyChange('DEVICE').pipe(
    map(({ languageCode }) => {
      return languageCode;
    },
      distinctUntilChanged())
  );
};

function onCookieUpdate(handler: (cookieAgreement: boolean) => void) {
  return onStateKeyChange('DEVICE').pipe(
    map(deviceState => {
      const cookie = deviceState.cookieAgreement;
      handler(cookie)
    })
  )
};



export {
  updateDeviceState,
  initializeApp,
  updateCookieAgreement,
  updateLanguage,
  acceptAllCookies,
  declineAllCookies,
  saveCustomCookiePreferences,
  onLanguageChange,
  onCookieUpdate
};
